import type { ReactElement } from "react";
import { faChartLine } from "@fortawesome/free-solid-svg-icons";
import { useShownColorScheme } from "hooks/useShownColorScheme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Combobox,
  DefaultMantineColor,
  Group,
  InputBase,
  Menu,
  SegmentedControl,
  Text,
  useCombobox,
  useMantineTheme,
} from "@mantine/core";
import { Routing } from "config/enums/routings";
import { useTranslation } from "react-i18next";
import { getYAxisOptions } from "utils/chart/yAxisOptions";
import optionClasses from "components/UI/SelectableOption.module.css";
import classes from "./ChartPanelMenu.module.css";

type Props = {
  routing: Routing;
  xAxisType: string | null;
  yAxisType: string | null;
  setXAxisType: (value: string | null) => void;
  setYAxisType: (value: string | null) => void;
  getYAxis: () => (string | null)[] | [];
  getTooltipYAxisIcon: (color?: DefaultMantineColor) => ReactElement;
  disabledXAxisToggle: boolean;
};

export const ChartPanelMenu = ({
  xAxisType,
  yAxisType,
  setXAxisType,
  setYAxisType,
  getYAxis,
  getTooltipYAxisIcon,
  disabledXAxisToggle,
}: Props) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const computedColorScheme = useShownColorScheme();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: (eventSource) => {
      if (eventSource === "keyboard") {
        combobox.selectActiveOption();
      } else {
        combobox.updateSelectedOptionIndex("active");
      }
    },
  });

  return (
    <Menu
      shadow="md"
      position="top"
      withinPortal
      closeOnItemClick={false}
      styles={{ dropdown: { left: undefined, right: "0px" } }}
      classNames={{
        item: classes.menuItem,
        itemLabel: classes.menuItemLabel,
        itemSection: classes.menuItemSection,
      }}
    >
      <Menu.Target>
        <ActionIcon
          variant="light"
          className={classes.button}
          aria-label={t("chart_options_menu.button_label")}
        >
          {!yAxisType ? (
            <FontAwesomeIcon icon={faChartLine} />
          ) : (
            getTooltipYAxisIcon(theme.primaryColor)
          )}
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          component="div"
          rightSection={
            <Combobox
              store={combobox}
              resetSelectionOnOptionHover
              withinPortal={false}
              onOptionSubmit={(value) => {
                setYAxisType(value);
                combobox.updateSelectedOptionIndex("active");
                combobox.closeDropdown();
              }}
            >
              <Combobox.Target targetType="button">
                <InputBase
                  component="button"
                  type="button"
                  pointer
                  // Fills the item section, the dropdown list takes the same width
                  w="100%"
                  rightSection={<Combobox.Chevron />}
                  rightSectionPointerEvents="none"
                  onClick={() => combobox.toggleDropdown()}
                >
                  <Group wrap="nowrap">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        width: "100%",
                      }}
                    >
                      <span style={{ width: "0.75rem" }}>
                        {
                          getYAxisOptions(
                            t,
                            undefined,
                            computedColorScheme === "dark" ? "inherit" : "#000"
                          ).find((item) => item.value === yAxisType)?.icon
                        }
                      </span>
                      <Text size="sm">
                        {
                          getYAxisOptions(t).find(
                            (item) => item.value === yAxisType
                          )?.label
                        }
                      </Text>
                    </div>
                  </Group>
                </InputBase>
              </Combobox.Target>
              <Combobox.Dropdown>
                <Combobox.Options>
                  {getYAxisOptions(t, getYAxis()).map(
                    ({ value, label, icon }) => {
                      const isSelected = yAxisType === value;

                      return (
                        <Combobox.Option
                          className={optionClasses.option}
                          mod={{ selected: isSelected }}
                          value={value}
                          key={value}
                          active={yAxisType === value}
                        >
                          <Group wrap="nowrap">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                width: "100%",
                              }}
                            >
                              <span style={{ width: "0.75rem" }}>{icon}</span>
                              <Text size="sm">{label}</Text>
                            </div>
                          </Group>
                        </Combobox.Option>
                      );
                    }
                  )}
                </Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
          }
        >
          {t("chart_options_menu.y_axis_type.label")}
        </Menu.Item>
        <Menu.Item
          component="div"
          rightSection={
            <SegmentedControl
              size="xs"
              data={[
                {
                  label: t("chart_options_menu.x_axis_type.distance"),
                  value: "distance",
                },
                {
                  label: t("chart_options_menu.x_axis_type.time"),
                  value: "time",
                },
              ]}
              onChange={(value) => {
                if (!disabledXAxisToggle) setXAxisType(value);
              }}
              value={xAxisType ?? undefined}
              disabled={disabledXAxisToggle}
            />
          }
        >
          {t("chart_options_menu.x_axis_type.label")}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
